import React, { useContext, useMemo } from "react";
import "./bot-icon.css";
import { CommonContext } from "hooks/CommonContext";
import { toAbsoluteUrl } from "utils/commonUtils";
import PropTypes from "prop-types";
import { HiSparkles } from "react-icons/hi";

const BotAgentSpeaker = ({ flicker }) => {
  const { userOwnData, participantOfMeeting, aiAgentDetail } =
    useContext(CommonContext);

  const isInterviewerView = 
    !userOwnData.isCandidate && 
    participantOfMeeting.filter((user) => user.isJoin).length > 0;

  // Calculate dynamic values based on flicker with smoother scaling
  const glowIntensity = Math.min(flicker / 3, 50);
  const pulseSize = Math.min(3 + flicker / 25, 12);
  const opacity = Math.min(0.4 + flicker / 150, 0.8);
  
  // Stabilize speaking status - only change if flicker is significantly high
  const isSpeaking = useMemo(() => flicker > 80, [flicker]);

  return (
    <div 
      className="bot-icon-visualizer d-flex flex-column align-items-center justify-content-center"
      style={{ 
        position: "relative",
        padding: "60px 20px 20px 20px", // Extra top padding for label
      }}
    >
      {/* AI Label - positioned above everything */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          padding: "6px 16px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "600",
          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          zIndex: 10,
        }}
      >
        <HiSparkles size={14} />
        AI Interviewer
      </div>

      {/* Container for circles and image */}
      <div style={{ position: "relative", display: "inline-block" }}>
        {/* Outer Pulse Rings - behind the main circle */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isInterviewerView ? "140px" : "260px",
            height: isInterviewerView ? "140px" : "260px",
            borderRadius: "50%",
            border: "2px solid rgba(102, 126, 234, 0.2)",
            animation: "pulse 2s ease-in-out infinite",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isInterviewerView ? "160px" : "290px",
            height: isInterviewerView ? "160px" : "290px",
            borderRadius: "50%",
            border: "1px solid rgba(102, 126, 234, 0.1)",
            animation: "pulse 2s ease-in-out infinite 0.5s",
            zIndex: 0,
          }}
        />

        {/* Main AI Circle */}
        <div
          className={`${
            isInterviewerView
              ? "for-interviewer-ai-circle"
              : "ai-circle"
          }`}
          style={{
            boxShadow: `
              0 0 ${glowIntensity}px ${glowIntensity / 2}px rgba(102, 126, 234, ${opacity}),
              0 0 ${glowIntensity * 1.5}px ${glowIntensity * 0.8}px rgba(118, 75, 162, ${opacity / 2})
            `,
            border: `${pulseSize}px solid rgba(102, 126, 234, 0.6)`,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* AI Image */}
          <div
            className="w-100 h-100 overflow-hidden"
            style={{ 
              borderRadius: "50%",
              position: "relative",
            }}
          >
            <img
              className="w-100 h-100"
              alt="AI Agent"
              src={toAbsoluteUrl(aiAgentDetail.imagePath)}
              style={{ objectFit: "cover" }}
            />
          </div>
        </div>
      </div>

      {/* AI Status Text - Below the circle */}
      <div
        style={{
          marginTop: "20px",
          textAlign: "center",
        }}
      >
        <div style={{ 
          fontSize: "14px", 
          fontWeight: "600", 
          color: "#667eea", 
          marginBottom: "6px" 
        }}>
          {aiAgentDetail.name || "AI Assistant"}
        </div>
        <div 
          style={{ 
            fontSize: "13px", 
            color: isSpeaking ? "#10b981" : "#6c757d",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "color 0.3s ease",
          }}
        >
          <span 
            style={{ 
              width: "8px", 
              height: "8px", 
              borderRadius: "50%", 
              background: isSpeaking ? "#10b981" : "#cbd5e1",
              animation: isSpeaking ? "blink 1.5s ease-in-out infinite" : "none",
              transition: "background 0.3s ease",
            }}
          />
          {isSpeaking ? "Speaking..." : "Listening"}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.4;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.03);
            opacity: 0.6;
          }
        }

        @keyframes blink {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }
      `}</style>
    </div>
  );
};

BotAgentSpeaker.propTypes = {
  flicker: PropTypes.number.isRequired,
};

export default BotAgentSpeaker;